import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { Text } from '@/nova/components';
import { CFG, CFG_ANIM, CFG_CHROME } from '../../../../../theme/configurator-tokens';
import { cardHover, popupIn, thinScrollbar } from '../../../../../theme/configurator-keyframes';
import { USER_MENU } from './UserMenu.tokens';

/**
 * Fixed anchor at the top-right of the preview pane (just left of the docked
 * sidebar). The icon is its only laid-out child; the popover is absolutely
 * positioned beneath it and right-aligned, so the popover's right edge always
 * lines up with the icon's right edge (per the Figma spec).
 */
export const Root = styled.div`
  position: fixed;
  top: ${USER_MENU.anchorTop};
  /* Default anchor leaves room for the docked sidebar; the full-bleed payment-
     success screen overrides this var to pull the icon to the viewport edge. */
  right: var(--cfg-usermenu-right, ${USER_MENU.anchorRight});
  z-index: 40;
  /* The account icon (rightmost) + the optional Home shortcut to its left sit on
     one row; the popover is absolutely positioned and right-aligned to this box's
     right edge — i.e. the account icon — so its Figma alignment is unchanged. */
  display: flex;
  align-items: center;
  gap: 8px;
  height: ${USER_MENU.iconButton};
`;

export const Trigger = styled.button<{ $open: boolean }>`
  display: flex;
  width: ${USER_MENU.iconButton};
  height: ${USER_MENU.iconButton};
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  color: ${USER_MENU.fg};
  background: ${({ $open }) => ($open ? CFG_CHROME.chipHover : CFG.base100)};
  transition: background-color 150ms ease;

  &:hover {
    background: ${CFG_CHROME.chipHover};
  }
`;

/**
 * "Back to Owner Studio" shortcut, sitting just left of the account icon and
 * shown only for owners who already have other brands. Mirrors the account
 * `Trigger`'s resting chrome (same 32px pill, foreground colour, hover wash) so
 * the pair reads as one control group. A router `Link` — it navigates, it does
 * not toggle — so it carries no `$open` state.
 */
export const HomeButton = styled(Link)`
  display: flex;
  width: ${USER_MENU.iconButton};
  height: ${USER_MENU.iconButton};
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 16px;
  cursor: pointer;
  color: ${USER_MENU.fg};
  background: ${CFG.base100};
  transition: background-color 150ms ease;

  &:hover {
    background: ${CFG_CHROME.chipHover};
  }
`;

export const Popup = styled.div`
  position: absolute;
  top: calc(100% + 16px);
  right: 0;
  width: ${USER_MENU.popupWidth};
  display: flex;
  flex-direction: column;
  padding: 0 16px 16px;
  border-radius: 24px;
  background: ${USER_MENU.surface};
  box-shadow: 0 24px 48px ${CFG.scrimDeep};

  /* Unfolds out of the account icon (top-right) on open. */
  transform-origin: top right;
  animation: ${popupIn} ${CFG_ANIM.POPUP_MS}ms ${CFG_ANIM.EASE_POP} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 16px 8px 16px 0;
  opacity: 0.3;
`;

export const SectionLabelText = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  text-transform: uppercase;
  color: ${USER_MENU.fg};
`;

export const Divider = styled.div<{ $spaced?: boolean }>`
  height: 1px;
  width: 100%;
  margin-top: ${({ $spaced }) => ($spaced ? '12px' : '0')};
  background: linear-gradient(to right, transparent, ${CFG_CHROME.dividerVia}, transparent);
`;

export const Field = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
`;

export const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${CFG.base300};
`;

export const FieldLabelText = styled(Text)`
  font-size: 14px;
  line-height: 1.43;
  color: ${CFG.base300};
`;

const boxBase = `
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 8px 12px;
  border-radius: 12px;
  background: ${CFG.base100};
`;

export const ReadonlyBox = styled.div`
  ${boxBase}
`;

export const EmailText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${CFG.base400};
  word-break: break-all;
`;

export const SelectBox = styled.button<{ $open: boolean }>`
  ${boxBase}
  width: 100%;
  justify-content: space-between;
  gap: 1px;
  border: 1px solid ${({ $open }) => ($open ? CFG.hairlineStrong : 'transparent')};
  cursor: pointer;
  color: ${USER_MENU.fg};
  transition: background-color 150ms ease, border-color 150ms ease;

  &:hover {
    background: ${CFG_CHROME.chipHover};
  }
`;

export const ValueText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${USER_MENU.fg};
`;

/** Chevron wrapper — flips when the select is open. */
export const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  flex-shrink: 0;
  color: ${USER_MENU.fg};
  transition: transform 150ms ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

export const OptionList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  margin-top: 4px;
  max-height: 196px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid ${CFG.hairlineStrong};
  background: ${CFG.surfaceRaised};
  box-shadow: 0 16px 32px ${CFG.scrimDeep};
  ${thinScrollbar};
`;

export const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  background: transparent;
  transition: background-color 150ms ease;

  &:hover {
    background: ${CFG.washHover};
  }
`;

export const OptionText = styled(Text)<{ $selected: boolean }>`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: ${({ $selected }) => ($selected ? CFG.brandText : USER_MENU.fg)};
`;

export const Check = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${CFG.brandText};
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  margin-top: 12px;
  padding: 8px 16px;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}
  background: ${USER_MENU.logoutBg};
  transition: background-color 150ms ease;

  &:hover {
    background: ${USER_MENU.logoutHover};
  }
`;

export const LogoutText = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${USER_MENU.logoutText};
`;
